import { ExerciseData, LevelDescription } from "./types";

export const getInitialLevel = (level: string): number => {
  switch (level.toLowerCase()) {
    case "absolute beginner":
      return 5; // Absolute Beginner (A0) - Midpoint
    case "beginner":
      return 15; // Beginner (A1) - Midpoint
    case "intermediate":
      return 45; // Lower Intermediate (B1-) - Midpoint
    case "advanced":
      return 75; // Advanced (C1-) - Midpoint
    default:
      return 35;
  }
};

export const getDifficultyCategory = (level: number): string => {
  if (level <= 30) return "beginner";
  if (level <= 60) return "intermediate";
  return "advanced";
};

export const getLevelDescription = (level: number): LevelDescription => {
  if (level <= 10) return "Absolute Beginner (A0)";
  if (level <= 20) return "Beginner (A1)";
  if (level <= 30) return "Elementary (A2)";
  if (level <= 40) return "Lower Intermediate (B1-)";
  if (level <= 50) return "Intermediate (B1)";
  if (level <= 60) return "Upper Intermediate (B2)";
  if (level <= 70) return "Advanced (C1-)";
  if (level <= 80) return "Proficient (C1)";
  if (level <= 90) return "Near-Native (C2-)";
  return "Native/Full Mastery (C2+)";
};

export const usesNonLatinScript = (
  languageCode: string,
  languageName?: string,
  exerciseData?: ExerciseData
): boolean => {
  // If exercise data is provided, only return true if transcription exists
  if (exerciseData && !exerciseData.transcription) {
    return false;
  }

  // Languages that use non-Latin scripts
  const nonLatinLanguages = [
    "ar",
    "ar-SA",
    "ar-EG",
    "ar-AE", // Arabic
    "zh",
    "zh-CN",
    "zh-TW",
    "zh-HK", // Chinese
    "ja",
    "ja-JP", // Japanese
    "ko",
    "ko-KR", // Korean
    "ru",
    "ru-RU", // Russian
    "hi",
    "hi-IN", // Hindi
    "th",
    "th-TH", // Thai
    "he",
    "he-IL", // Hebrew
    "fa",
    "fa-IR", // Persian/Farsi
    "ur",
    "ur-PK", // Urdu
    "bn",
    "bn-BD",
    "bn-IN", // Bengali
    "ta",
    "ta-IN",
    "ta-LK", // Tamil
    "te",
    "te-IN", // Telugu
    "ml",
    "ml-IN", // Malayalam
    "kn",
    "kn-IN", // Kannada
    "gu",
    "gu-IN", // Gujarati
    "pa",
    "pa-IN", // Punjabi
    "ne",
    "ne-NP", // Nepali
    "si",
    "si-LK", // Sinhala
    "my",
    "my-MM", // Myanmar/Burmese
    "ka",
    "ka-GE", // Georgian
    "am",
    "am-ET", // Amharic
    "km",
    "km-KH", // Khmer
    "lo",
    "lo-LA", // Lao
  ];

  // Check by language code first
  if (nonLatinLanguages.some((code) => languageCode.toLowerCase().startsWith(code.toLowerCase()))) {
    return true;
  }

  // Check by language name as fallback
  const nonLatinNames = [
    "arabic",
    "chinese",
    "mandarin",
    "cantonese",
    "japanese",
    "korean",
    "russian",
    "hindi",
    "thai",
    "hebrew",
    "persian",
    "farsi",
    "urdu",
    "bengali",
    "tamil",
    "telugu",
    "malayalam",
    "kannada",
    "gujarati",
    "punjabi",
    "nepali",
    "sinhala",
    "myanmar",
    "burmese",
    "georgian",
    "amharic",
    "khmer",
    "lao",
  ];

  if (!languageName) return false;

  return nonLatinNames.some((name) => languageName.toLowerCase().includes(name));
};

export const normalizeAnswer = (answer: string): string => {
  return answer
    .toLowerCase()
    .trim()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "") // Remove diacritics
    .replace(/[íìîï]/g, "i")
    .replace(/[áàâãä]/g, "a")
    .replace(/[éèêë]/g, "e")
    .replace(/[óòôõö]/g, "o")
    .replace(/[úùûü]/g, "u")
    .replace(/[ñ]/g, "n")
    .replace(/[.,!?¡¿'"]/g, "")
    .replace(/\s+/g, "");
};

export const getMistakePenalty = (consecutiveMistakes: number): number => {
  return Math.min(Math.max(0, consecutiveMistakes) + 1, 3);
};

export const levenshteinDistance = (a: string, b: string): number => {
  if (a.length === 0) return b.length;
  if (b.length === 0) return a.length;

  const matrix = Array(b.length + 1)
    .fill(null)
    .map(() => Array(a.length + 1).fill(null));

  for (let i = 0; i <= a.length; i++) matrix[0][i] = i;
  for (let j = 0; j <= b.length; j++) matrix[j][0] = j;

  for (let j = 1; j <= b.length; j++) {
    for (let i = 1; i <= a.length; i++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      matrix[j][i] = Math.min(
        matrix[j][i - 1] + 1,
        matrix[j - 1][i] + 1,
        matrix[j - 1][i - 1] + cost
      );
    }
  }

  return matrix[b.length][a.length];
};

// Level progress storage functions
export const saveLevelProgress = (language: string, languageCode: string, level: number) => {
  try {
    const storageKey = `chicky-level-progress-${language}-${languageCode}`;
    const progressData = {
      level,
      timestamp: Date.now(),
    };
    localStorage.setItem(storageKey, JSON.stringify(progressData));
  } catch (error) {
    console.error("Failed to save level progress:", error);
  }
};

export const loadLevelProgress = (language: string, languageCode: string): number | null => {
  try {
    const storageKey = `chicky-level-progress-${language}-${languageCode}`;
    const stored = localStorage.getItem(storageKey);
    if (stored) {
      const progressData = JSON.parse(stored);
      // Only return the level if it was saved within the last 30 days
      const thirtyDaysAgo = Date.now() - 30 * 24 * 60 * 60 * 1000;
      if (progressData.timestamp > thirtyDaysAgo) {
        return progressData.level;
      } else {
        // Remove expired progress
        localStorage.removeItem(storageKey);
      }
    }
  } catch (error) {
    console.error("Failed to load level progress:", error);
  }
  return null;
};
